/**
 * Team headshots for grid scorecards.
 * Photos live in /public/profile-photos/ (team folders + staff archive).
 *
 * Order within each team is the display order Bryan set for the grid stacks.
 * Sales Coordinators are always last.
 */

const BULLPEN_BISONS = [
  "/profile-photos/bullpen-bisons/meredith-johnston.png",
  "/profile-photos/bullpen-bisons/tyler-hale.jpeg",
  "/profile-photos/bullpen-bisons/amy-koenig.png",
  "/profile-photos/bullpen-bisons/tymara-goldwin.jpeg",
] as const;

const TEAM_MEATBALL = [
  "/profile-photos/team-meatball/adam-quait.png",
  "/profile-photos/team-meatball/moose-norris.png",
  "/profile-photos/team-meatball/sebastian-loreti.png",
  "/profile-photos/team-meatball/sydney.png",
] as const;

const MERCH_MOB = [
  "/profile-photos/merch-mob/tanner-wilson.jpeg",
  "/profile-photos/merch-mob/chantelle-sirignano.png",
  "/profile-photos/merch-mob/lindsey-queen.png",
  "/profile-photos/merch-mob/priscilla-pollacco.png",
  "/profile-photos/merch-mob/bayden-bell.png",
] as const;

const TEAM_BEYOND = [
  "/profile-photos/team-beyond/chris-lorence.png",
  "/profile-photos/team-beyond/allison-goode.png",
  "/profile-photos/team-beyond/brittney-barker.png",
  "/profile-photos/team-beyond/katrina-leach.png",
] as const;

const INSIDE_SALES = [
  "/profile-photos/inside-sales/sarah-scott.png",
  "/profile-photos/inside-sales/vhinz-pardo.jpeg",
  "/profile-photos/inside-sales/ryan-harold.png",
] as const;

const REGAL_EAGLES = [
  "/profile-photos/regal-eagles/nathan-beach.png",
  "/profile-photos/regal-eagles/zach-kercz.jpeg",
] as const;

/** Sales team photos keyed by warehouse team display name. */
export const SALES_TEAM_PHOTOS: Record<string, readonly string[]> = {
  "Bullpen Bisons": BULLPEN_BISONS,
  "Team Meatball": TEAM_MEATBALL,
  "Merch Mob": MERCH_MOB,
  "Team Beyond": TEAM_BEYOND,
  "Inside Sales": INSIDE_SALES,
  "Regal Eagles": REGAL_EAGLES,
};

/** Grid headshot stack order — keep in sync with photo arrays above. */
export const SALES_TEAM_ROSTER_ORDER: Record<string, readonly string[]> = {
  "Bullpen Bisons": ["Meredith Johnston", "Tyler Hale", "Amy Koenig", "Tymara Goldwin"],
  "Team Meatball": ["Adam Quait", "Moose Norris", "Sebastian Loreti", "Sydney Winterhalter"],
  "Merch Mob": [
    "Tanner Wilson",
    "Chantelle Sirignano",
    "Lindsey Queen",
    "Priscilla Pollacco",
    "Bayden Bell",
  ],
  "Team Beyond": ["Chris Lorence", "Allison Goode", "Brittney Barker", "Katrina Leach"],
  "Inside Sales": ["Sarah Scott", "Vhinz Pardo", "Ryan Harold"],
  "Regal Eagles": ["Nathan Beach", "Zach Kercz"],
};

/** Sort rep rows to match grid headshot order; unknown names fall to the end. */
export function sortByTeamRosterOrder<T extends { label: string }>(
  teamName: string,
  items: T[],
): T[] {
  const order = SALES_TEAM_ROSTER_ORDER[teamName];
  if (!order?.length) return items;

  const rank = new Map(order.map((name, index) => [name.toLowerCase(), index]));
  return [...items].sort((a, b) => {
    const aRank = rank.get(a.label.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
    const bRank = rank.get(b.label.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
    if (aRank !== bRank) return aRank - bRank;
    return a.label.localeCompare(b.label);
  });
}

/** SDR photos keyed by warehouse team display name (one headshot per SDR team). */
export const SDR_TEAM_PHOTOS: Record<string, readonly string[]> = {
  Megan: ["/profile-photos/sdr/megan.png"],
};

export function getTeamPhotos(teamName: string, department: "sales" | "sdr"): string[] {
  const map = department === "sales" ? SALES_TEAM_PHOTOS : SDR_TEAM_PHOTOS;
  return [...(map[teamName] ?? [])];
}
